<script lang="ts" setup>
import { inject, ref, watch, onBeforeUnmount, provide, onMounted } from 'vue';
import { Viewer, Animations, Glow, HideSmallMeshes, SceneInfo } from '@immersive-tech/viewer-engine';
import * as BuilderExtensions from '@/viewerExtensions';
import EngineInfo from '@/components/Viewer/EngineInfo.vue';
import ViewerResize from '@/components/Viewer/ViewerResize.vue';

import type { Ref } from 'vue';
import type { IViewerOptions, Nullable } from '@immersive-tech/viewer-engine';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const canvasElement = ref<Nullable<HTMLCanvasElement>>(null);
const canvasWidth = ref(0);
const canvasHeight = ref(0);
const viewerInitialized = ref(false);

const viewerOptions: IViewerOptions = {
  pathToWASM: `${window.location.origin}/`,
  debugEvents: false,
  optimizations: {
    meshFreeze: false,
    worldMatrixFreeze: false,
    materialFreeze: false,
  },
};

const initializeViewer = () => {
  if (!canvasElement.value) return;
  if (viewer.value) return;

  viewer.value = new Viewer(canvasElement.value, viewerOptions, [
    [Animations, 'Animations'],
    [Glow, 'Glow'],
    [HideSmallMeshes, 'HideSmallMeshes'],
    [SceneInfo, 'SceneInfo'],
    [BuilderExtensions.Debugger, 'Debugger'],
    [BuilderExtensions.Lights, 'Lights'],
    [BuilderExtensions.ManifestHandler, 'ManifestHandler'],
    [BuilderExtensions.Perspectives, 'Perspectives'],
    [BuilderExtensions.HotspotPicker, 'HotspotPicker'],
    [BuilderExtensions.Hotspots, 'Hotspots'],
    [BuilderExtensions.CameraTargetHelper, 'CameraTargetHelper'],
  ]);

  viewerInitialized.value = true;
};

const canvasElementWatcher = (newValue: Nullable<HTMLCanvasElement>) => {
  if (!newValue) return;
  if (viewer.value) return;

  canvasWidth.value = newValue.clientWidth;
  canvasHeight.value = newValue.clientHeight;

  initializeViewer();

  window.builderViewer = viewer;
};

const unmountHandler = () => {
  if (!viewer.value) return;
};

const mountHandler = () => {
  if (!viewer.value) return;
  if (!canvasElement.value) return;

  viewerInitialized.value = true;
};

provide('canvasElement', canvasElement);

watch(canvasElement, canvasElementWatcher);
onBeforeUnmount(unmountHandler);
onMounted(mountHandler);
</script>

<template>
  <div id="render-canvas-container" class="h-full w-full bg-gray-600" style="max-width: calc(100vw - 385px);max-height: calc(100vh - 37px)">
    <canvas id="render-canvas" ref="canvasElement" class="w-full h-full outline-0 m-auto" />
    <template v-if="viewerInitialized === true">
      <EngineInfo />
      <ViewerResize />
    </template>
  </div>
</template>
